import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/app/environments/environments';
import { RemoteApiService } from './remote-api.service';
import { AccountPlan, AssignUserPlanRequest, PlanConfig, PlanId, sortPlansByPosition } from '../models/plan.model';

@Injectable({ providedIn: 'root' })
export class PlanService {
  private readonly base = environment.stripeManagerUrl;
  private readonly adminBase = environment.administratorManager;

  constructor(private ras: RemoteApiService) {}

  /** Launch UI: Free + Pro only (monthly). */
  listLaunchPlans(): Observable<PlanConfig[]> {
    return this.ras
      .getApi(`${this.base}/list-pricing?expose=launch&interval=month`)
      .pipe(
        map((res: any) => sortPlansByPosition((res?.data || []) as PlanConfig[])),
        catchError(() => of([])),
      );
  }

  listAnnualPlans(): Observable<PlanConfig[]> {
    return this.ras
      .getApi(`${this.base}/list-pricing?expose=launch&interval=year`)
      .pipe(
        map((res: any) => sortPlansByPosition((res?.data || []) as PlanConfig[])),
        catchError(() => of([])),
      );
  }

  comparePlans(): Observable<PlanConfig[]> {
    return this.ras.getApi(`${this.base}/compare-plans`).pipe(
      map((res: any) => sortPlansByPosition((res?.data || []) as PlanConfig[])),
      catchError(() => of([])),
    );
  }

  getAccountPlan(userId: string): Observable<AccountPlan | null> {
    return this.ras.getApi(`${this.base}/account-plan/${userId}`).pipe(
      map((res: any) => (res?.data || null) as AccountPlan | null),
      catchError(() => of(null)),
    );
  }

  /** SuperAdmin: assign manual or custom plan without Stripe checkout. */
  assignUserPlan(
    userId: string,
    body: AssignUserPlanRequest,
  ): Observable<{ success: boolean; result?: unknown }> {
    return this.ras
      .postApi(`${this.adminBase}/users/${userId}/assign-plan`, body)
      .pipe(catchError((err) => of({ success: false, result: err })));
  }

  hasChannel(plan: PlanConfig | null | undefined, channelId: string): boolean {
    const normalized = channelId === 'facebook' ? 'messenger' : channelId;
    const channels = (plan?.channels_available || []).map((c) =>
      c === 'facebook' ? 'messenger' : c,
    );
    return channels.includes(normalized);
  }

  hasFeature(plan: PlanConfig | null | undefined, feature: keyof PlanConfig['features']): boolean {
    return !!plan?.features?.[feature];
  }

  usagePercent(used: number, limit: number): number {
    if (!limit || limit <= 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  }

  resolvePlanFromBilling(billing: any): PlanId {
    if (!billing) return 'free';
    return (billing.plan_id || billing.type || 'free').toLowerCase() as PlanId;
  }
}
